import React, { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { useVerifiedPhoneNumbersStore } from "@/stores/verifiedPhoneNumbersStore";
import { api } from "@/lib/api";
import { VerifiedPhoneNumber, OrganizationWithUsers } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Spinner } from "@/components/ui/spinner";
import ReportSubscriptionsCard from "@/components/settings/ReportSubscriptionsCard";
import { useCurrentOrgRole, canAccessApcmOps } from "@/lib/useCurrentOrgRole";

// No need for a custom type now that we added the field to the original interface
// interface OrganizationWithSettings extends Omit<OrganizationWithUsers, 'default_apcm_strategy_version_id'> {
//   default_apcm_strategy_version_id?: string;
// }

interface OrganizationSettingsProps {
  // Rendered inside the Settings page, which supplies its own header and padding
  embedded?: boolean;
}

const OrganizationSettings = ({ embedded = false }: OrganizationSettingsProps) => {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useOrganizationsStore();
  const {
    phoneNumbers,
    defaultPhoneNumberId,
    isLoading: isLoadingPhoneNumbers,
    fetchVerifiedPhoneNumbers,
    setDefaultPhoneNumber,
  } = useVerifiedPhoneNumbersStore();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] =
    useState<string>("");
  const [strategyVersionId, setStrategyVersionId] = useState<string>("");
  const [orgTimezone, setOrgTimezone] = useState<string>("");
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);
  const role = useCurrentOrgRole();
  const canManageReports = canAccessApcmOps(role, !!user?.is_superuser);
  const [isSavingPhoneNumber, setIsSavingPhoneNumber] = useState(false);
  const [isSavingStrategyVersion, setIsSavingStrategyVersion] = useState(false);

  useEffect(() => {
    if (!selectedOrganizationId) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch organization to get the APCM strategy version ID
        const org = await api.getOrganization(selectedOrganizationId);
        if (org) {
          setStrategyVersionId(org.default_apcm_strategy_version_id || "");
          setOrgTimezone(org.timezone || "");
        }

        // Fetch verified phone numbers
        await fetchVerifiedPhoneNumbers(selectedOrganizationId);
      } catch (error) {
        console.error("Failed to fetch organization settings:", error);
        toast({
          title: "Failed to fetch settings",
          description: "There was an error loading the organization settings.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedOrganizationId, fetchVerifiedPhoneNumbers]);

  // Update selected phone number when default changes
  useEffect(() => {
    if (defaultPhoneNumberId) {
      setSelectedPhoneNumberId(defaultPhoneNumberId);
    }
  }, [defaultPhoneNumberId]);

  const handleSavePhoneNumber = async () => {
    if (!selectedOrganizationId || !selectedPhoneNumberId) return;

    setIsSavingPhoneNumber(true);
    try {
      await setDefaultPhoneNumber(
        selectedOrganizationId,
        selectedPhoneNumberId
      );

      toast({
        title: "Default phone number updated",
        description:
          "The organization default phone number has been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to save default phone number:", error);
      toast({
        title: "Failed to save settings",
        description: "There was an error saving the default phone number.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPhoneNumber(false);
    }
  };

  const handleSaveStrategyVersionId = async () => {
    if (!selectedOrganizationId || !strategyVersionId) return;

    setIsSavingStrategyVersion(true);
    try {
      await api.updateOrganizationAPCMStrategy(
        selectedOrganizationId,
        strategyVersionId
      );

      toast({
        title: "Strategy version updated",
        description: "The APCM strategy version has been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to save strategy version:", error);
      toast({
        title: "Failed to save settings",
        description: "There was an error saving the strategy version ID.",
        variant: "destructive",
      });
    } finally {
      setIsSavingStrategyVersion(false);
    }
  };

  const handleSaveTimezone = async () => {
    if (!selectedOrganizationId || !orgTimezone.trim()) return;
    setIsSavingTimezone(true);
    try {
      await api.updateOrganization(selectedOrganizationId, { timezone: orgTimezone.trim() });
      toast({
        title: "Time zone updated",
        description: "Report days, weeks and months now follow this zone.",
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      toast({
        title: "Failed to save time zone",
        description: Array.isArray(detail) ? detail[0]?.msg : typeof detail === "string" ? detail : undefined,
        variant: "destructive",
      });
    } finally {
      setIsSavingTimezone(false);
    }
  };

  if (!selectedOrganizationId) {
    return (
      <div className="container mx-auto px-4 py-6 md:py-10">
        <Card>
          <CardHeader>
            <CardTitle>No Organization Selected</CardTitle>
            <CardDescription>
              Please select an organization to manage its settings.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-6" : "container mx-auto px-4 py-6 md:py-10 space-y-6"}>
      {!embedded && <h1 className="text-2xl md:text-3xl font-bold">Organization Settings</h1>}

      {isLoading || isLoadingPhoneNumbers ? (
        <div className="flex justify-center p-10">
          <Spinner size="large" />
        </div>
      ) : (
        <>
          {/* APCM Strategy Version ID - Only visible to superusers */}
          {user?.is_superuser && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  APCM Strategy Version ID
                </CardTitle>
                <CardDescription>
                  Set the default strategy version ID for APCM calls.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <Input
                    placeholder="Strategy Version ID"
                    value={strategyVersionId}
                    onChange={(e) => setStrategyVersionId(e.target.value)}
                    className="max-w-md"
                  />
                  <Button
                    onClick={handleSaveStrategyVersionId}
                    disabled={isSavingStrategyVersion || !strategyVersionId}
                  >
                    {isSavingStrategyVersion ? (
                      <Spinner className="mr-2" size="small" />
                    ) : null}
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Verified Phone Numbers - Visible to all users */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Default Phone Number</CardTitle>
              <CardDescription>
                Select the default phone number to use for outbound calls.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {phoneNumbers.length === 0 ? (
                <p className="text-muted-foreground">
                  No verified phone numbers available. Please contact your
                  administrator.
                </p>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <Select
                    value={selectedPhoneNumberId}
                    onValueChange={setSelectedPhoneNumberId}
                  >
                    <SelectTrigger className="max-w-md">
                      <SelectValue placeholder="Select a phone number" />
                    </SelectTrigger>
                    <SelectContent>
                      {phoneNumbers.map((number) => (
                        <SelectItem key={number.id} value={number.id}>
                          {number.phone_number}{" "}
                          {number.is_default ? "(Default)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleSavePhoneNumber}
                    disabled={
                      isSavingPhoneNumber ||
                      !selectedPhoneNumberId ||
                      selectedPhoneNumberId === defaultPhoneNumberId
                    }
                  >
                    {isSavingPhoneNumber ? (
                      <Spinner className="mr-2" size="small" />
                    ) : null}
                    Set as Default
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {canManageReports && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Time zone</CardTitle>
                  <CardDescription>
                    IANA zone the clinic operates in, e.g. America/Chicago. Used to decide
                    where each reporting day, week and month starts and ends.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                    <Input
                      placeholder="America/Chicago"
                      value={orgTimezone}
                      onChange={(e) => setOrgTimezone(e.target.value)}
                      className="max-w-md"
                    />
                    <Button
                      onClick={handleSaveTimezone}
                      disabled={isSavingTimezone || !orgTimezone.trim()}
                    >
                      {isSavingTimezone ? <Spinner className="mr-2" size="small" /> : null}
                      Save
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <ReportSubscriptionsCard
                organizationId={selectedOrganizationId}
                currentUserEmail={user?.email}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default OrganizationSettings;
